import { useState } from 'react';
import { X, CreditCard, Receipt, Loader2, User, Mail, Phone, Building, MapPin, FileText, Hash } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutModal = ({ isOpen, onClose, plan, billingPeriod, user, onSoldOut }) => {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: user?.email || '',
    whatsapp: '',
    richiedi_fattura: false,
    ragione_sociale: '',
    indirizzo_fatturazione: '',
    codice_fiscale: '',
    partita_iva: '',
    codice_sdi: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) newErrors.nome = 'Nome richiesto';
    if (!formData.cognome.trim()) newErrors.cognome = 'Cognome richiesto';
    if (!formData.email.trim()) newErrors.email = 'Email richiesta';
    if (!formData.whatsapp.trim()) newErrors.whatsapp = 'Numero cellulare richiesto';
    
    // Validate invoice fields if requested
    if (formData.richiedi_fattura) {
      if (!formData.ragione_sociale.trim()) newErrors.ragione_sociale = 'Ragione sociale richiesta';
      if (!formData.indirizzo_fatturazione.trim()) newErrors.indirizzo_fatturazione = 'Indirizzo richiesto';
      if (!formData.codice_fiscale.trim() && !formData.partita_iva.trim()) {
        newErrors.codice_fiscale = 'Codice Fiscale o P.IVA richiesto';
      }
      if (!formData.codice_sdi.trim()) newErrors.codice_sdi = 'Codice SDI richiesto';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan,
          billing_period: billingPeriod,
          origin_url: window.location.origin,
          user_id: user?.id,
          email: formData.email,
          nome: formData.nome,
          cognome: formData.cognome,
          whatsapp: formData.whatsapp,
          richiedi_fattura: formData.richiedi_fattura,
          ragione_sociale: formData.ragione_sociale,
          indirizzo_fatturazione: formData.indirizzo_fatturazione,
          codice_fiscale: formData.codice_fiscale,
          partita_iva: formData.partita_iva,
          codice_sdi: formData.codice_sdi
        })
      });

      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        if (response.status === 409 && onSoldOut) onSoldOut();
        throw new Error(data.detail || 'Errore durante la creazione del checkout');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setErrors({ submit: error.message || 'Errore durante il checkout' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const planNames = {
    fondatori: 'Founder Club',
    standard: 'Standard',
    premium: 'Premium'
  };

  const planPrices = {
    fondatori: { monthly: '6,90', quarterly: null, annual: '69,90' },
    founder: { monthly: '6,90', quarterly: null, annual: '69,90' },
    standard: { monthly: '15,00', quarterly: '38,90', annual: '120,00' },
    premium: { monthly: '21,00', quarterly: '54,00', annual: '169,90' }
  };

  const periodNames = { monthly: 'mese', quarterly: 'trimestre', annual: 'anno' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#4A7C59]" />
            </div>
            <div>
              <h2 className="font-bold text-[#2C3E50]">Completa l'acquisto</h2>
              <p className="text-sm text-[#64748B]">
                Piano {planNames[plan]} - €{planPrices[plan]?.[billingPeriod] || '0'}/{periodNames[billingPeriod]}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-[#F4F7F5] rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-[#64748B]" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-[#2C3E50]">I tuoi dati</p>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1">Nome *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Mario"
                    className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all ${errors.nome ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                  />
                </div>
                {errors.nome && <p className="text-xs text-red-500 mt-1">{errors.nome}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1">Cognome *</label>
                <input
                  type="text"
                  name="cognome"
                  value={formData.cognome}
                  onChange={handleChange}
                  placeholder="Rossi"
                  className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all ${errors.cognome ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                />
                {errors.cognome && <p className="text-xs text-red-500 mt-1">{errors.cognome}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#64748B] mb-1">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="mario.rossi@email.it"
                  className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all ${errors.email ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                />
              </div>
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-[#64748B] mb-1">Numero Cellulare *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  placeholder="+39 333 1234567"
                  className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all ${errors.whatsapp ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                />
              </div>
              <p className="text-xs text-[#9CA3AF] mt-1">Per le notifiche push sull'assistenza</p>
              {errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}
            </div>
          </div>

          {/* Invoice Option */}
          <div className="border-t border-[#E5E7EB] pt-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${formData.richiedi_fattura ? 'bg-[#4A7C59] border-[#4A7C59]' : 'border-[#D1D5DB] group-hover:border-[#4A7C59]'}`}>
                {formData.richiedi_fattura && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <input
                type="checkbox"
                name="richiedi_fattura"
                checked={formData.richiedi_fattura}
                onChange={handleChange}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-[#4A7C59]" />
                <span className="text-sm font-medium text-[#2C3E50]">Richiedi Fattura</span>
              </div>
            </label>
            <p className="text-xs text-[#9CA3AF] ml-8 mt-1">
              Seleziona se hai bisogno della fattura per la tua azienda
            </p>
          </div>

          {/* Invoice Fields (conditional) */}
          {formData.richiedi_fattura && (
            <div className="space-y-3 p-4 bg-[#F4F7F5] rounded-xl border border-[#E5E7EB]">
              <p className="text-sm font-semibold text-[#2C3E50] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#4A7C59]" />
                Dati di Fatturazione
              </p>
              
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1">Ragione Sociale / Nome Completo *</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    name="ragione_sociale"
                    value={formData.ragione_sociale}
                    onChange={handleChange}
                    placeholder="Azienda Srl o Mario Rossi"
                    className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all bg-white ${errors.ragione_sociale ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                  />
                </div>
                {errors.ragione_sociale && <p className="text-xs text-red-500 mt-1">{errors.ragione_sociale}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1">Indirizzo Completo *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    name="indirizzo_fatturazione"
                    value={formData.indirizzo_fatturazione}
                    onChange={handleChange}
                    placeholder="Via Roma 1, 00100 Roma (RM)"
                    className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all bg-white ${errors.indirizzo_fatturazione ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                  />
                </div>
                {errors.indirizzo_fatturazione && <p className="text-xs text-red-500 mt-1">{errors.indirizzo_fatturazione}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#64748B] mb-1">Codice Fiscale</label>
                  <input
                    type="text"
                    name="codice_fiscale"
                    value={formData.codice_fiscale}
                    onChange={handleChange}
                    placeholder="RSSMRA80A01H501U"
                    className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all bg-white uppercase ${errors.codice_fiscale ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                  />
                  {errors.codice_fiscale && <p className="text-xs text-red-500 mt-1">{errors.codice_fiscale}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#64748B] mb-1">Partita IVA</label>
                  <input
                    type="text"
                    name="partita_iva"
                    value={formData.partita_iva}
                    onChange={handleChange}
                    placeholder="IT12345678901"
                    className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all bg-white uppercase"
                  />
                </div>
              </div>
              <p className="text-xs text-[#9CA3AF]">Inserisci Codice Fiscale e/o Partita IVA</p>

              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1">Codice SDI (Codice Destinatario) *</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    name="codice_sdi"
                    value={formData.codice_sdi}
                    onChange={handleChange}
                    placeholder="0000000 o XXXXXXX"
                    className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#4A7C59]/20 focus:border-[#4A7C59] outline-none transition-all bg-white uppercase ${errors.codice_sdi ? 'border-red-500' : 'border-[#E5E7EB]'}`}
                  />
                </div>
                <p className="text-xs text-[#9CA3AF] mt-1">Usa 0000000 se non hai un codice SDI</p>
                {errors.codice_sdi && <p className="text-xs text-red-500 mt-1">{errors.codice_sdi}</p>}
              </div>
            </div>
          )}

          {/* Error Message */}
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#4A7C59] hover:bg-[#3A6345] text-white font-semibold rounded-xl transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Reindirizzamento a Stripe...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Procedi al Pagamento
              </>
            )}
          </Button>

          <p className="text-xs text-center text-[#9CA3AF]">
            Pagamento sicuro con Stripe. IVA 22% inclusa nel prezzo.
          </p>
        </form>
      </div>
    </div>
  );
};

export default CheckoutModal;
